import Together from 'together-ai';
import { useLanguage } from '../contexts/LanguageContext';

const client = new Together({
  apiKey: import.meta.env.VITE_TOGETHER_API_KEY,
});

// Rate limiting configuration
const RETRY_DELAY = 2000; // 2 seconds
const MAX_RETRIES = 3;

// Helper function to delay execution
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Helper function to make API calls with retry logic
async function makeAPICall<T>(
  apiCall: () => Promise<T>,
  retries = MAX_RETRIES
): Promise<T> {
  try {
    return await apiCall();
  } catch (error: any) {
    if (error?.status === 429 && retries > 0) {
      console.log(`Rate limited, retrying in ${RETRY_DELAY}ms... (${retries} retries left)`);
      await delay(RETRY_DELAY);
      return makeAPICall(apiCall, retries - 1);
    }
    throw error;
  }
}

interface TestCase {
  input: string;
  expectedOutput: string;
  isHidden?: boolean;
}

interface Challenge {
  title: string;
  description: string;
  examples: {
    input: string;
    output: string;
    explanation: string;
  }[];
  difficulty: 'Easy' | 'Medium' | 'Hard';
  hints: string[];
  testCases: TestCase[];
  functionTemplate: string;
  solutionCode: string;
}

export const generateChallenge = async (topic: string): Promise<Challenge> => {
  try {
    const prompt = `Create a C# coding challenge about ${topic}.
Return a JSON object with EXACTLY this structure (replace the example values):

{
  "title": "Array Sum Calculator",
  "description": "Write a function that calculates the sum of all elements in an integer array. Handle null and empty arrays by returning 0.",
  "difficulty": "Easy",
  "examples": [
    {
      "input": "[1, 2, 3]",
      "output": "6",
      "explanation": "1 + 2 + 3 = 6"
    }
  ],
  "hints": [
    "Consider using LINQ's Sum() method",
    "Don't forget to handle edge cases"
  ],
  "testCases": [
    {
      "input": "[1, 2, 3]",
      "expectedOutput": "6",
      "isHidden": false
    }
  ],
  "functionTemplate": "public class Solution { public static int Process(int[] numbers) { // Your code here\\nreturn 0; } }",
  "solutionCode": "public class Solution { public static int Process(int[] numbers) { return numbers?.Sum() ?? 0; } }"
}

Important:
1. Use realistic C# code examples
2. Include multiple test cases
3. Make sure all JSON properties match exactly
4. Escape newlines in code with \\n
5. Return ONLY the JSON object, no other text`;

    const response = await makeAPICall(() => 
      client.chat.completions.create({
        model: 'meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo',
        messages: [
          {
            role: 'system',
            content: 'You are a C# challenge generator that returns only valid JSON objects. No additional text or explanations.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.1,
        max_tokens: 2000,
        top_p: 0.95
      })
    );

    const content = response.choices[0]?.message?.content;
    if (!content) {
      console.log('No content received, using fallback');
      return getFallbackChallenge();
    }

    try {
      // Clean the content
      const cleanedContent = content
        .replace(/```json\s*|\s*```/g, '') // Remove code blocks
        .replace(/[\u200B-\u200D\uFEFF]/g, '') // Remove zero-width spaces
        .trim();

      // Try to find and extract JSON object
      const jsonMatch = cleanedContent.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        console.log('No JSON object found in response:', cleanedContent);
        return getFallbackChallenge();
      }

      const result = JSON.parse(jsonMatch[0]);

      // Validate required fields
      if (!result.title || !result.description || !result.difficulty || 
          !Array.isArray(result.examples) || !Array.isArray(result.testCases)) {
        console.log('Missing required fields in response:', result);
        return getFallbackChallenge();
      }

      // Ensure proper formatting of code
      const formattedTemplate = result.functionTemplate
        .replace(/\\n/g, '\n')
        .replace(/\\/g, '');
      const formattedSolution = result.solutionCode
        .replace(/\\n/g, '\n')
        .replace(/\\/g, '');

      return {
        title: result.title,
        description: result.description,
        difficulty: result.difficulty as 'Easy' | 'Medium' | 'Hard',
        examples: result.examples.map((ex: any) => ({
          input: String(ex.input),
          output: String(ex.output),
          explanation: String(ex.explanation)
        })),
        hints: Array.isArray(result.hints) ? result.hints : [],
        testCases: result.testCases.map((test: any) => ({
          input: String(test.input),
          expectedOutput: String(test.expectedOutput),
          isHidden: Boolean(test.isHidden)
        })),
        functionTemplate: formattedTemplate || defaultTemplate,
        solutionCode: formattedSolution || defaultTemplate
      };
    } catch (parseError) {
      console.error('Error parsing challenge:', parseError);
      console.log('Raw content:', content);
      return getFallbackChallenge();
    }
  } catch (error) {
    console.error('Error generating challenge:', error);
    return getFallbackChallenge();
  }
};

const defaultTemplate = `using System;
using System.Linq;

public class Solution {
    public static int Process(int[] input) {
        // Your code here
        return 0;
    }
}`;

const getFallbackChallenge = (): Challenge => ({
  title: "Array Sum Calculator",
  description: "Create a function that calculates the sum of all elements in an integer array.",
  difficulty: "Easy",
  examples: [
    {
      input: "[1, 2, 3, 4, 5]",
      output: "15",
      explanation: "1 + 2 + 3 + 4 + 5 = 15"
    }
  ],
  hints: [
    "Consider using LINQ's Sum() method",
    "Don't forget to handle null arrays",
    "Think about edge cases like empty arrays"
  ],
  testCases: [
    {
      input: "[1, 2, 3, 4, 5]",
      expectedOutput: "15",
      isHidden: false
    },
    {
      input: "[]",
      expectedOutput: "0",
      isHidden: false
    },
    {
      input: "[100, 200, 300]",
      expectedOutput: "600",
      isHidden: true
    }
  ],
  functionTemplate: `using System;
using System.Linq;

public class Solution {
    public static int Sum(int[] numbers) {
        // Your code here
        return 0;
    }
}`,
  solutionCode: `using System;
using System.Linq;

public class Solution {
    public static int Sum(int[] numbers) {
        return numbers?.Sum() ?? 0;
    }
}`
});

export const executeCode = async (code: string, testCases: TestCase[]): Promise<TestResult[]> => {
  try {
    const prompt = `Execute this C# code with the given test cases and return results as JSON array.

Code:
${code}

Test Cases:
${JSON.stringify(testCases, null, 2)}

Return ONLY a JSON array in this exact format (no additional text or explanations):
[
  {
    "input": "test input",
    "expectedOutput": "expected output",
    "actualOutput": "actual output",
    "passed": true,
    "isHidden": false
  }
]`;

    const response = await makeAPICall(() => 
      client.chat.completions.create({
        model: 'meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo',
        messages: [
          {
            role: 'system',
            content: 'You are a C# code executor. Return only a JSON array of test results without any additional text or explanations.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.1,
        max_tokens: 2000,
        top_p: 0.95
      })
    );

    const content = response.choices[0]?.message?.content;
    if (!content) {
      throw new Error('No content received from API');
    }

    // Clean and extract JSON array
    const cleanedContent = content.trim()
      .replace(/```json\s*|\s*```/g, '')  // Remove code blocks
      .replace(/[\r\n]/g, '') // Remove newlines
      .replace(/^[^[]*(\[[\s\S]*\])[^\]]*$/, '$1'); // Extract JSON array

    try {
      const results = JSON.parse(cleanedContent);
      
      // Validate results structure
      if (!Array.isArray(results)) {
        throw new Error('Response is not an array');
      }

      return results.map(result => ({
        input: String(result.input || ''),
        expectedOutput: String(result.expectedOutput || ''),
        actualOutput: String(result.actualOutput || ''),
        passed: Boolean(result.passed),
        isHidden: Boolean(result.isHidden)
      }));
    } catch (parseError) {
      console.error('JSON Parse Error:', parseError);
      console.log('Raw Content:', content);
      console.log('Cleaned Content:', cleanedContent);
      
      // Return simulated results if parsing fails
      return testCases.map(test => ({
        input: test.input,
        expectedOutput: test.expectedOutput,
        actualOutput: 'Error executing code',
        passed: false,
        isHidden: test.isHidden
      }));
    }
  } catch (error) {
    console.error('Error executing code:', error);
    
    // Return error results
    return testCases.map(test => ({
      input: test.input,
      expectedOutput: test.expectedOutput,
      actualOutput: error instanceof Error ? error.message : 'Unknown error',
      passed: false,
      isHidden: test.isHidden
    }));
  }
};

interface TestResult {
  input: string;
  expectedOutput: string;
  actualOutput: string;
  passed: boolean;
  isHidden?: boolean;
}

const InterviewTypes = {
  technical: 'technical',
  behavioral: 'behavioral',
  system: 'system',
  pair: 'pair'
} as const;

const ExperienceLevels = {
  junior: 'junior',
  mid: 'mid',
  senior: 'senior'
} as const;

export const generateInterviewPrompt = (
  interviewType: keyof typeof InterviewTypes,
  experienceLevel: keyof typeof ExperienceLevels,
  companyType: string,
  language: string
) => {
  const { t } = useLanguage();
  
  const basePrompt = t('ai.interviewer.role')
    .replace('{type}', t(`interview.${interviewType}` as const))
    .replace('{level}', t(`level.${experienceLevel}` as const))
    .replace('{company}', companyType);

  return `${basePrompt}

Language: ${language}
Please conduct this interview in ${language === 'en' ? 'English' : language === 'fr' ? 'French' : 'Arabic'}.

Start the interview with:
1. A brief introduction as the interviewer
2. Context about the role and company
3. Your first question based on the interview type

For Technical interviews: Focus on coding and architecture
For Behavioral: Focus on past experiences and scenarios
For System Design: Focus on scalability and design patterns
For Pair Programming: Present a collaborative coding challenge

Format your response using markdown with proper sections, code blocks, and formatting.`;
};


